import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  LayoutDashboard, Type, Image, Plus, Trash2, Save, RefreshCw, Eye, EyeOff, X, MessageSquareText
} from "lucide-react";



const SERVER_URL = "https://allinone-clone-7d5d795e98ce.herokuapp.com";

const AdminTestimonials = () => {
  const [data, setData] = useState({
    sectionInfo: { subTitle: "", mainTitle: "" },
    testimonials: [],
    isActive: true,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Fetch Data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${SERVER_URL}/api/testimonials`);
        if (res.data.success) {
          setData(res.data.data);
        }
      } catch (err) {
        showMessage("Failed to fetch data", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  // Handle Section Info Change
  const handleSectionChange = (field, value) => {
    setData(prev => ({
      ...prev,
      sectionInfo: {
        ...prev.sectionInfo,
        [field]: value
      }
    }));
  };

  // Handle Testimonial Item Change
  const handleItemChange = (index, field, value) => {
    const newItems = [...data.testimonials];
    newItems[index] = { ...newItems[index], [field]: value };
    setData({ ...data, testimonials: newItems });
  };

  const addItem = () => {
    const newItem = {
      id: Date.now().toString(),
      quote: "",
      clientName: "",
      clientRole: "",
      clientImage: ""
    };
    setData({ ...data, testimonials: [...data.testimonials, newItem] });
  };

  const removeItem = (index) => {
    const newItems = data.testimonials.filter((_, i) => i !== index);
    setData({ ...data, testimonials: newItems });
  };

  // Submit Form
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await axios.put(`${SERVER_URL}/api/testimonials`, data);
      showMessage("Section updated successfully!", "success");
    } catch (err) {
      showMessage("Failed to update", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset to default settings?")) return;
    try {
      const res = await axios.post(`${SERVER_URL}/api/testimonials/reset`);
      if (res.data.success) {
        setData(res.data.data);
        showMessage("Reset successful", "success");
      }
    } catch (err) {
      showMessage("Failed to reset", "error");
    }
  };

  const toggleStatus = async () => {
    try {
      await axios.patch(`${SERVER_URL}/api/testimonials/toggle`);
      const newStatus = !data.isActive;
      setData({ ...data, isActive: newStatus });
      showMessage(`Section ${newStatus ? "activated" : "deactivated"}`, "success");
    } catch (err) {
      showMessage("Failed to toggle status", "error");
    }
  };

  if (loading) return <div className="p-5 text-center">Loading Admin Panel...</div>;

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <h3 className="fw-bold">Testimonials Section Management</h3>
        <div className="d-flex gap-2">
            <button 
                className={`btn btn-sm ${data.isActive ? 'btn-success' : 'btn-outline-secondary'} d-flex align-items-center gap-2`}
                onClick={toggleStatus}
                type="button"
            >
                {data.isActive ? <Eye size={18} /> : <EyeOff size={18} />}
                {data.isActive ? "Active" : "Disabled"}
            </button>
          <button className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2" onClick={handleReset} disabled={saving} type="button">
            <RefreshCw size={18} /> Reset
          </button>
          <button className="btn btn-primary btn-sm d-flex align-items-center gap-2" onClick={handleSubmit} disabled={saving} type="button">
            <Save size={18} /> {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'error' ? 'danger' : message.type} mb-4`}>
          {message.text}
          <button type="button" className="btn-close float-end" onClick={() => setMessage(null)}></button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row">
          
          {/* Section Content */}
          <div className="col-lg-12 mb-4">
            <div className="card shadow-sm h-100">
                <div className="card-header bg-white fw-bold py-3 d-flex align-items-center gap-2">
                    <Type size={20} /> Section Titles
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label className="form-label small fw-bold">Sub Title</label>
                            <input
                              type="text"
                              className="form-control"
                              value={data.sectionInfo?.subTitle || ""}
                              onChange={(e) => handleSectionChange('subTitle', e.target.value)}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label small fw-bold">Main Title</label>
                            <input
                              type="text"
                              className="form-control"
                              value={data.sectionInfo?.mainTitle || ""}
                              onChange={(e) => handleSectionChange('mainTitle', e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>
          </div>

          {/* Testimonials List */}
          <div className="col-lg-12 mb-4">
            <div className="card shadow-sm">
                <div className="card-header bg-white fw-bold py-3 d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                        <MessageSquareText size={20} /> Testimonials
                    </div>
                    <button type="button" className="btn btn-sm btn-primary d-flex align-items-center gap-1" onClick={addItem}>
                        <Plus size={16} /> Add New
                    </button>
                </div>
                <div className="card-body">
                    {data.testimonials && data.testimonials.map((item, index) => (
                        <div key={item.id || index} className="mb-4 pb-4 border-bottom position-relative">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span className="badge bg-secondary">Testimonial #{index + 1}</span>
                                <button type="button" className="btn btn-outline-danger btn-sm d-flex align-items-center justify-content-center" onClick={() => removeItem(index)}>
                                    <Trash2 size={16} />
                                </button>
                            </div>
                            
                            <div className="row g-3">
                                <div className="col-12">
                                    <label className="small text-muted mb-1">Quote</label>
                                    <textarea
                                      className="form-control"
                                      rows="2"
                                      placeholder="Quote"
                                      value={item.quote || ""}
                                      onChange={(e) => handleItemChange(index, 'quote', e.target.value)}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="small text-muted mb-1">Client Name</label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="Client Name"
                                      value={item.clientName || ""}
                                      onChange={(e) => handleItemChange(index, 'clientName', e.target.value)}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="small text-muted mb-1">Client Role/Company</label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="Client Role/Company"
                                      value={item.clientRole || ""}
                                      onChange={(e) => handleItemChange(index, 'clientRole', e.target.value)}
                                    />
                                </div>
                                <div className="col-12">
                                    <label className="small text-muted mb-1">Client Image URL</label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="Client Image URL"
                                      value={item.clientImage || ""}
                                      onChange={(e) => handleItemChange(index, 'clientImage', e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                    {(!data.testimonials || data.testimonials.length === 0) && (
                        <p className="text-center text-muted py-3 mb-0">No testimonials added.</p>
                    )}
                </div>
            </div>
          </div>

        </div>
      </form>
    </div>
  );
};

export default AdminTestimonials;